import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ExperimentalInputs,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import AddConditionsModal from 'studies/typed/process-development/AddConditionsModal';
import MethodExecutionLimitError from 'studies/typed/process-development/MethodExecutionLimitError';
import ReplicateLimitLabel from 'studies/typed/process-development/ReplicateLimitLabel';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';
import stopPropagation from 'util/stopPropagation';

import {ExperimentFlowchart, ExperimentMethod} from './ExperimentalInputs';
import ExperimentPlan, {ExperimentalInputsContext} from './ExperimentPlan';
import {ReplicateTable} from './ReplicateTable';
import {MethodExecutionLimitState} from './useMethodExecutionLimitState';
import {Condition} from './useProcessDevelopmentStudyState';

export function getDefaultConditionNames(length: number, procedureType: ProcedureType): Array<string> {
  return Array.from(
    {length},
    (_, conditionNumber: number) =>
      `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} ${conditionNumber + 1}`
  );
}

export function getConditionCountsByName(names: Array<string>): Map<string, number> {
  const conditionCounts = new Map<string, number>();

  names.forEach((name) => {
    if (conditionCounts.has(name)) {
      // Increment count if name already exists
      conditionCounts.set(name, conditionCounts.get(name)! + 1);
    } else {
      // Initialize count if name does not exist
      conditionCounts.set(name, 1);
    }
  });

  return conditionCounts;
}

export function ExperimentDesignEditor(props: {
  conditions: Array<Condition>;
  setConditions: (conditions: Array<Condition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs | null;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  context: ExperimentalInputsContextProperties;
  selectedMethodId: string;
  setSelectedMethodId: (selectedMethodId: string) => void;
  procedureMethods: Array<ExperimentMethod>;
  procedureFlowchart: ExperimentFlowchart;
  methodExecutionLimitState: MethodExecutionLimitState;
  estimate: TimingEstimateResult;
  procedureType: ProcedureType;
}): JSX.Element {
  const {isOverLimit, replicateLimit, remainingReplicates} = props.methodExecutionLimitState;
  const isAtLimit = remainingReplicates === 0;

  function addConditions(numConditions: number, numReplicates: number): void {
    const conditionNames = getDefaultConditionNames(
      props.conditions.length + numConditions,
      props.procedureType
    );

    const existingConditionNames = props.conditions
      .map((condition) => condition.name)
      .concat(conditionNames.slice(props.conditions.length));

    const conditionCounts = getConditionCountsByName(existingConditionNames);

    props.setConditions(
      [
        ...props.conditions.map((condition) => ({
          ...condition,
          validationState: {
            ...condition.validationState,
            isDuplicateName: conditionCounts.get(condition.name)! > 1,
            isNameLengthOverLimit:
              condition.name.length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH,
            isDescLengthOverLimit:
              (condition.description ?? '').length >
              EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH,
          },
        })),
        ...Array.from({length: numConditions}, (_, index: number) => ({
          numReplicates,
          name: conditionNames[index + props.conditions.length],
          validationState: {
            isDuplicateName: conditionCounts.get(conditionNames[index + props.conditions.length])! > 1,
            isNameLengthOverLimit:
              conditionNames[index + props.conditions.length].length >
              EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH,
            isDescLengthOverLimit: false,
          },
        })),
      ],
      [...props.conditions.map((_, i) => i), ...new Array(numConditions).fill(null)]
    );
  }

  const lastNumReplicates =
    props.conditions.length > 0 ? props.conditions[props.conditions.length - 1].numReplicates : 1;

  const editNumColsButtons: JSX.Element = (
    <div className={css(styles.buttonContainer)}>
      <div
        className={css(styles.button)}
        // These controls will be embedded in a table header. Double-clicking on the table header will
        // toggle the table between the collapsed and expanded states. If the user clicks on any of these
        // controls in quick succession, a double-click event will bubble up to the table and cause it to
        // toggle its collapsed state. To prevent this, we prevent the propagation of the double-click event.
        onDoubleClick={stopPropagation}
      >
        <Tooltip
          text={`Cannot exceed ${replicateLimit} ${pluralize(replicateLimit, 'replicate')}`}
          isDisabled={!isAtLimit}
        >
          <ButtonGroup>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              icon="compose"
              size={ButtonSize.MINI}
              onClick={() => {
                addConditions(1, lastNumReplicates);
              }}
              disabled={isAtLimit}
              id="add-condition-button"
            />
            <Dropdown
              menu={
                <Menu width={MenuWidth.SMALL}>
                  <Option
                    dataTestAttrs={{
                      component: 'AddConditionsDropdown',
                      element: 'addMultipleConditionsButton',
                    }}
                    onSelect={() => {
                      runModal(AddConditionsModal, {
                        onRequestSubmit: ({numConditions, numReplicates}) => {
                          addConditions(numConditions, numReplicates);
                        },
                        remainingReplicates,
                        replicateLimit,
                        initialReplicates: lastNumReplicates,
                        procedureType: props.procedureType,
                      });
                    }}
                  >
                    {`Add multiple ${getProcedureRunConditionTextForProcedureType(props.procedureType, {
                      plural: true,
                    })}`}
                  </Option>
                </Menu>
              }
            >
              <Button
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                size={ButtonSize.MINI}
                isDropdown={true}
                hideDropdownCaret={true}
                icon="chevron-down"
                iconAltText="Show dropdown menu"
                dataTestAttrs={{component: 'AddConditionsDropdown', element: 'addMultipleConditionsDropdown'}}
                disabled={isAtLimit}
                id="add-multiple-conditions-dropdown-button"
              />
            </Dropdown>
          </ButtonGroup>
        </Tooltip>
      </div>
      <ReplicateLimitLabel replicateLimit={replicateLimit} />
    </div>
  );
  return (
    <>
      <div className={css(styles.container)}>
        <div className={css(styles.header)}>
          <ReplicateTable
            conditions={props.conditions}
            setConditions={props.setConditions}
            selectedMethodId={props.selectedMethodId}
            experimentalInputs={props.experimentalInputs}
            setExperimentalInputs={props.setExperimentalInputs}
            editNumColsButtons={editNumColsButtons}
            locked={props.context.locked}
            estimate={props.estimate}
            procedureType={props.procedureType}
            dataTestAttrs={{component: 'ReplicateTable', element: 'replicateTable'}}
          />
          {isOverLimit && (
            <div className={css(styles.banner)}>
              <MethodExecutionLimitError
                replicateLimit={replicateLimit}
                procedureType={props.procedureType}
              />
            </div>
          )}
        </div>
        <div className={css(styles.body)}>
          <ExperimentalInputsContext.Provider value={props.context}>
            <ExperimentPlan
              methods={props.procedureMethods}
              procedureFlowchart={props.procedureFlowchart}
              selectedMethodId={props.selectedMethodId}
              onChangeSelectedMethod={props.setSelectedMethodId}
              procedureType={props.procedureType}
            />
          </ExperimentalInputsContext.Provider>
        </div>
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    flex: 0,
    borderBottom: '1px solid #e0e0e0',
    paddingLeft: 10,
    // paddingRight: 10 is already handled by .mediocre-tableEditable-titleBarWrapper
  },
  body: {
    flex: 1,
    position: 'relative',
    minHeight: 0,
  },
  button: {
    paddingTop: '3px',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: '12px',
    marginLeft: 12,
  },
  banner: {
    paddingBottom: '9px',
    display: 'inline-block',
  },
});
